/**
 * canvas-engine/renderer/CanvasRenderer.jsx
 *
 * OWN unified Canvas Engine renderer — a single component that renders a canvas
 * section in either `builder` mode (Console in-process Preview) or `reader` mode
 * (published page). One renderer, two modes → Preview ↔ Reader parity by
 * construction. (Req 20.1, 20.4)
 *
 * ZERO REUSE: imports NOTHING from `admin-console-ui/src/modules/template-builder/`
 * or `academy-ui/src/template/`. It composes ONLY its own canvas-engine siblings
 * (model primitives, the per-type element registry, and the section error
 * boundary).
 *
 * Responsibilities:
 *   - Resolve a complete, valid Canvas_Model from `section.content.canvas` via
 *     the own `normalizeCanvas`; if it still fails `validateCanvas` (shouldn't
 *     happen post-normalize, but guarded), paint a DEFINED fallback frame and
 *     return. (Req 24.1)
 *   - Render the fixed Reference_Width frame: a positioned container sized to
 *     `canvas.width × canvas.height`, applying frame background / radius /
 *     shadow / padding and an optional full-frame background image. The frame is
 *     `position: relative` and is the positioning context for elements. (Req 2.6)
 *   - Lay out every NON-hidden element in array order via an absolute wrapper
 *     computed from `x / y / width / height / zIndex` (+ `rotation`). Resolve the
 *     component from the own `getElementRenderer`; unknown types are skipped.
 *     Each element render is wrapped in the own `CanvasSectionErrorBoundary` so a
 *     single element's throw cannot abort the page. (Req 24.2, 24.3)
 *   - `reader` mode: emit CONTENT ONLY — no selection wrappers, outlines,
 *     handles, toolbars, or `data-*` selection hooks. (Req 20.5, 20.6, 20.7)
 *   - `builder` mode: render the SAME content DOM + the SAME wrapper geometry,
 *     adding only non-layout-affecting selection affordances on the wrapper
 *     (click handler, `data-canvas-element-*` attributes, and an outline /
 *     box-shadow on the selected element). The inner element DOM and the
 *     computed left/top/width/height are byte-identical to reader mode, so
 *     Preview ↔ Reader parity holds (Property 13).
 *
 * Pure-ish presentational component: no data fetching. `data` and `theme` are
 * passed straight through to each element renderer (for data-bound elements).
 *
 * Custom HTML / CSS (Custom_Code_Section): section-level custom CSS is NOT a
 * canvas-level field. The custom decomposition adapter (`presets/
 * decompositionAdapters/custom.js`) represents both Custom HTML and Custom CSS
 * sections as a single `html` Canvas_Element whose `content.html` carries the
 * markup and `content.css` carries the author CSS. That element is dispatched
 * to the own `HtmlElement`, which sanitizes the HTML (`sanitizeHtml`), scopes
 * the CSS to a stable element-derived `#${scopeId}` (`scopeCustomCss`), and
 * wraps the content in that id with `isolation: isolate`. Because both run the
 * SAME pure functions in both modes, the scoped CSS + sanitized HTML are
 * byte-identical in builder and reader. The CanvasRenderer therefore needs NO
 * additional section-level custom-CSS wiring — it only owns the section frame
 * and element positioning. (Req 4.3, 26.1, 26.2, 26.3)
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { normalizeCanvas, validateCanvas } from '../model/canvasModel';
import { assetRef } from '../model/assetRef';
import { getElementRenderer } from './elements';
import { elementBackground, elementLinkProps } from './elements/elementLink';
import { elementAnimationStyle } from './elements/elementAnimation';
import CanvasSectionErrorBoundary from './CanvasSectionErrorBoundary';
import { applyDrag, applyResize, clampToFrame, DEFAULT_MIN_SIZE } from './editGeometry';

// ── Small pure helpers ──────────────────────────────────────────────────────

/** Finite number or fallback. */
function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/** Non-empty CSS string or fallback. */
function cssStr(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

/** A stable selection-outline that does NOT affect layout (drawn outside box). */
const SELECTION_OUTLINE = '2px solid #2563eb';
const SELECTION_SHADOW = '0 0 0 2px rgba(37,99,235,.35)';

/**
 * Fill style for the convert-to-link anchor so the link affordance covers the
 * element box exactly without altering the inner content's appearance.
 */
const ELEMENT_LINK_STYLE = Object.freeze({
  display: 'block',
  width: '100%',
  height: '100%',
  color: 'inherit',
  textDecoration: 'none',
});

/** Resolve a stable section identifier for selection callbacks. */
function resolveSectionId(section) {
  if (section && typeof section === 'object') {
    if (typeof section.sectionId === 'string' && section.sectionId.length > 0) return section.sectionId;
    if (typeof section.id === 'string' && section.id.length > 0) return section.id;
    if (typeof section.type === 'string' && section.type.length > 0) return section.type;
  }
  return 'canvas';
}

// ── Frame styling ───────────────────────────────────────────────────────────

/**
 * Build the frame container style from a normalized canvas model. The frame is
 * the fixed Reference_Width box and the positioning context for elements.
 */
function buildFrameStyle(canvas) {
  const style = {
    position: 'relative',
    boxSizing: 'border-box',
    width: num(canvas.width, 0),
    height: num(canvas.height, 0),
    margin: '0 auto',
    background: cssStr(canvas.background, 'transparent'),
    borderRadius: num(canvas.radius, 0),
    boxShadow: cssStr(canvas.shadow, 'none'),
    padding: cssStr(canvas.padding, 0),
    overflow: 'hidden',
  };

  // Optional full-frame background image, layered over the background color.
  if (canvas.backgroundImage) {
    const asset = assetRef(canvas.backgroundImage);
    const url = asset.displayLink || asset.storedLink || '';
    if (url) {
      style.backgroundImage = `url(${url})`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
      style.backgroundRepeat = 'no-repeat';
    }
  }

  return style;
}

function useResponsiveCanvasScale(enabled, width) {
  const shellRef = useRef(null);
  const [scale, setScale] = React.useState(1);

  useEffect(() => {
    if (!enabled) {
      setScale(1);
      return undefined;
    }

    const update = () => {
      const shell = shellRef.current;
      const parent = shell ? shell.parentElement : null;
      const available = parent ? parent.clientWidth : 0;
      const next = Number.isFinite(width) && width > 0 && available > 0
        ? Math.min(1, available / width)
        : 1;
      setScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };

    update();
    const parent = shellRef.current ? shellRef.current.parentElement : null;
    const observer = typeof ResizeObserver !== 'undefined' && parent
      ? new ResizeObserver(update)
      : null;
    if (observer && parent) observer.observe(parent);
    window.addEventListener('resize', update);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [enabled, width]);

  return [shellRef, scale];
}

/**
 * Build the absolute wrapper style for one element from its positional fields.
 * IMPORTANT: this geometry is identical in builder and reader modes — only the
 * frame's positioning context and the element's own x/y/width/height/zIndex
 * (+ rotation) determine it. Builder affordances never enter this object.
 */
function buildElementWrapperStyle(element) {
  const style = {
    position: 'absolute',
    boxSizing: 'border-box',
    left: num(element.x, 0),
    top: num(element.y, 0),
    width: num(element.width, 0),
    height: num(element.height, 0),
    zIndex: num(element.zIndex, 0),
  };

  const rotation = num(element.rotation, 0);
  if (rotation !== 0) {
    style.transform = `rotate(${rotation}deg)`;
    style.transformOrigin = 'center center';
  }

  return style;
}

// ── Builder-only in-frame selection box (drag + 8 resize handles) ────────────
//
// Rendered ONLY in builder mode for the SELECTED element, as the LAST child of
// the `position:relative` section frame. Because it shares the EXACT same
// coordinate origin and uses the SAME left/top/width/height as the element
// wrapper, the box + handles align pixel-perfectly with the element in every
// stacked section (this is the fix for the old page-level overlay misalignment).
// The preview stage is rendered 1:1 (min-width 1180, never scaled) so a pointer
// delta in screen px equals a delta in canvas px.

/** Handle id → anchor (fraction of the box) + cursor. */
const HANDLE_DEFS = Object.freeze([
  { id: 'nw', left: '0%', top: '0%', cursor: 'nwse-resize' },
  { id: 'n', left: '50%', top: '0%', cursor: 'ns-resize' },
  { id: 'ne', left: '100%', top: '0%', cursor: 'nesw-resize' },
  { id: 'e', left: '100%', top: '50%', cursor: 'ew-resize' },
  { id: 'se', left: '100%', top: '100%', cursor: 'nwse-resize' },
  { id: 's', left: '50%', top: '100%', cursor: 'ns-resize' },
  { id: 'sw', left: '0%', top: '100%', cursor: 'nesw-resize' },
  { id: 'w', left: '0%', top: '50%', cursor: 'ew-resize' },
]);

const SELECTION_BOX_Z = 2147483646;

const HANDLE_BASE_STYLE = Object.freeze({
  position: 'absolute',
  width: 10,
  height: 10,
  marginLeft: -5,
  marginTop: -5,
  background: '#fff',
  border: '1.5px solid #2563eb',
  borderRadius: 2,
  boxSizing: 'border-box',
  pointerEvents: 'auto',
  zIndex: 1,
});

/** Extract a finite `{ x, y, width, height }` rect from an element. */
function rectOf(element) {
  const e = element && typeof element === 'object' ? element : {};
  return {
    x: num(e.x, 0),
    y: num(e.y, 0),
    width: num(e.width, 0),
    height: num(e.height, 0),
  };
}

/**
 * In-frame selection box for the selected element. Pointer drags on the body
 * move it; drags on a handle resize it. Geometry is computed via the engine's
 * own pure `editGeometry` and raised through `onChangeElementFrame(elementId,
 * { x, y, width, height })`. A `locked` element renders the box for feedback
 * but exposes no handles and ignores drags.
 *
 * @param {object} props
 * @param {object} props.element selected Canvas_Element ({ x, y, width, height, locked })
 * @param {{width:number,height:number}} props.frame the section frame size
 * @param {(elementId:string, frame:{x:number,y:number,width:number,height:number})=>void} props.onChangeElementFrame
 */
function SelectionBox({ element, frame, onChangeElementFrame }) {
  const interactionRef = useRef(null);
  const latestRef = useRef({ frame, onChangeElementFrame });
  latestRef.current = { frame, onChangeElementFrame };

  const locked = Boolean(element && element.locked);

  const handlePointerMove = useCallback((event) => {
    const it = interactionRef.current;
    if (!it) return;
    const { frame: f, onChangeElementFrame: emit } = latestRef.current;
    if (typeof emit !== 'function') return;

    const dx = event.clientX - it.startX;
    const dy = event.clientY - it.startY;

    if (it.kind === 'move') {
      const dragged = applyDrag(it.startRect, dx, dy, f);
      emit(it.elementId, {
        x: dragged.x,
        y: dragged.y,
        width: it.startRect.width,
        height: it.startRect.height,
      });
      return;
    }

    const next = applyResize(it.startRect, it.handle, dx, dy, f, DEFAULT_MIN_SIZE);
    const clamped = clampToFrame(next, f);
    emit(it.elementId, {
      x: clamped.x,
      y: clamped.y,
      width: next.width,
      height: next.height,
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    interactionRef.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerMove]);

  // Detach listeners if unmounted mid-drag.
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp],
  );

  const startInteraction = useCallback(
    (event, kind, handle) => {
      if (locked || !element) return;
      event.preventDefault();
      event.stopPropagation();
      interactionRef.current = {
        kind,
        handle: handle || null,
        startX: event.clientX,
        startY: event.clientY,
        startRect: rectOf(element),
        elementId: element.id,
      };
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [locked, element, handlePointerMove, handlePointerUp],
  );

  const rect = rectOf(element);
  const boxStyle = {
    position: 'absolute',
    left: rect.x,
    top: rect.y,
    width: rect.width,
    height: rect.height,
    boxSizing: 'border-box',
    outline: SELECTION_OUTLINE,
    outlineOffset: 0,
    boxShadow: SELECTION_SHADOW,
    zIndex: SELECTION_BOX_Z,
    cursor: locked ? 'default' : 'move',
    background: 'transparent',
    pointerEvents: 'auto',
  };

  return (
    <div
      className={`ce-canvas__selbox${locked ? ' ce-canvas__selbox--locked' : ''}`}
      style={boxStyle}
      data-canvas-selbox-id={element.id}
      onPointerDown={locked ? undefined : (event) => startInteraction(event, 'move', null)}
    >
      {locked
        ? null
        : HANDLE_DEFS.map((h) => (
            <span
              key={h.id}
              className="ce-canvas__handle"
              data-canvas-handle={h.id}
              style={{ ...HANDLE_BASE_STYLE, left: h.left, top: h.top, cursor: h.cursor }}
              onPointerDown={(event) => startInteraction(event, 'resize', h.id)}
            />
          ))}
    </div>
  );
}

// ── Fallback frame (Req 24.1) ────────────────────────────────────────────────

const FALLBACK_FRAME_STYLE = Object.freeze({
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  minHeight: 160,
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: '#f8fafc',
  border: '1px dashed #cbd5e1',
  borderRadius: 16,
  color: '#94a3b8',
  fontSize: 13,
  textAlign: 'center',
});

/** A defined, neutral fallback frame painted when canvas data is unrenderable. */
function FallbackFrame() {
  return (
    <div className="ce-canvas ce-canvas--fallback" style={FALLBACK_FRAME_STYLE} role="note" aria-label="canvas unavailable">
      Canvas unavailable
    </div>
  );
}

// ── Renderer ─────────────────────────────────────────────────────────────────

/**
 * Render a canvas section.
 *
 * @param {object}   props
 * @param {object}   props.section           the canvas section ({ content: { canvas } })
 * @param {('reader'|'builder')} [props.mode] render mode (default 'reader')
 * @param {*}        [props.data]             pass-through data for data-bound elements
 * @param {object}   [props.theme]            pass-through theme for elements
 * @param {function} [props.onSelectElement]  builder-only selection callback
 * @param {string}   [props.selectedElementId] builder-only selected element id
 * @param {function} [props.onChangeElementFrame] builder-only frame-change callback
 */
export default function CanvasRenderer({
  section,
  mode = 'reader',
  data,
  theme,
  onSelectElement,
  selectedElementId,
  onChangeElementFrame,
}) {
  const isBuilder = mode === 'builder';

  // Resolve a complete, valid model. normalizeCanvas tolerates any input.
  const canvas = normalizeCanvas(section && section.content ? section.content.canvas : undefined);
  const responsiveScaleEnabled = !isBuilder;
  const [responsiveShellRef, responsiveScale] = useResponsiveCanvasScale(
    responsiveScaleEnabled,
    num(canvas.width, 0),
  );

  // Guard: should never trip post-normalize, but paint a defined fallback if so.
  if (!validateCanvas(canvas)) {
    return <FallbackFrame />;
  }

  const sectionId = resolveSectionId(section);
  const frameStyle = buildFrameStyle(canvas);

  if (responsiveScaleEnabled) {
    frameStyle.transform = `scale(${responsiveScale})`;
    frameStyle.transformOrigin = 'top center';
    frameStyle.flex = '0 0 auto';
  }

  // BUILDER: the selected, non-hidden element (if any) gets an in-frame
  // selection box with drag + resize handles, rendered as the LAST frame child.
  const selectedElement =
    isBuilder && selectedElementId != null
      ? canvas.elements.find((el) => el && !el.hidden && el.id === selectedElementId) || null
      : null;
  const frameSize = { width: num(canvas.width, 0), height: num(canvas.height, 0) };

  const frame = (
    <div className="ce-canvas" style={frameStyle}>
      {canvas.elements.map((element, index) => {
        // Skip hidden elements entirely (no DOM emitted). (Req 24.2)
        if (element && element.hidden) return null;

        const Component = getElementRenderer(element && element.type);
        if (!Component) return null; // unknown type → skip

        const wrapperStyle = buildElementWrapperStyle(element);
        const key = (element && element.id) || `el-${index}`;

        // Per-element background override (additive): transparent toggle wins,
        // else an explicit color; absent ⇒ no override (current rendering). (Req 4.3, 4.8)
        const background = elementBackground(element && element.style);
        if (background != null) {
          wrapperStyle.background = background;
        }

        // Per-element entrance animation (additive): merge the resolved
        // animation inline style onto the wrapper box. Absent/`none` ⇒ no
        // animation (identity), preserving current rendering. The animation-name
        // matches the reader keyframes mounted via AnimationKeyframes. (Req 6.7, 6.8, 6.9)
        const animationStyle = elementAnimationStyle(element && element.animation);
        if (animationStyle) {
          Object.assign(wrapperStyle, animationStyle);
        }

        // Convert-to-link: resolve navigable anchor props when enabled. (Req 4.7, 5.6)
        const linkProps = elementLinkProps(element && element.content ? element.content.link : undefined);

        // Inner content is identical across modes; only wrapper affordances differ.
        const rendered = (
          <CanvasSectionErrorBoundary>
            <Component element={element} mode={mode} data={data} theme={theme} />
          </CanvasSectionErrorBoundary>
        );

        if (!isBuilder) {
          // READER: content only — no selection hooks, attrs, outlines, handlers.
          // When link-enabled, wrap the rendered output in a navigable anchor.
          const content = linkProps ? (
            <a
              className="ce-canvas__el-link"
              href={linkProps.href}
              target={linkProps.target}
              rel={linkProps.rel}
              style={ELEMENT_LINK_STYLE}
            >
              {rendered}
            </a>
          ) : (
            rendered
          );
          return (
            <div key={key} className="ce-canvas__el" style={wrapperStyle}>
              {content}
            </div>
          );
        }

        // BUILDER: render the SAME anchor affordance for parity, but prevent it
        // from navigating so the wrapper's selection click still wins. The click
        // bubbles to the wrapper's onClick handler below.
        const content = linkProps ? (
          <a
            className="ce-canvas__el-link"
            href={linkProps.href}
            target={linkProps.target}
            rel={linkProps.rel}
            style={ELEMENT_LINK_STYLE}
            onClick={(event) => event.preventDefault()}
          >
            {rendered}
          </a>
        ) : (
          rendered
        );

        // BUILDER: same wrapper geometry + non-layout-affecting affordances.
        const isSelected = selectedElementId != null && element.id === selectedElementId;
        const builderStyle = {
          ...wrapperStyle,
          cursor: 'pointer',
        };
        if (isSelected) {
          // outline + box-shadow are drawn outside the box → no layout shift.
          builderStyle.outline = SELECTION_OUTLINE;
          builderStyle.outlineOffset = '0px';
          builderStyle.boxShadow = SELECTION_SHADOW;
        }

        const handleSelect = (event) => {
          event.stopPropagation();
          if (typeof onSelectElement === 'function') {
            onSelectElement({
              sectionId,
              elementId: element.id,
              elementType: element.type,
              element,
            });
          }
        };

        return (
          <div
            key={key}
            className={`ce-canvas__el${isSelected ? ' ce-canvas__el--selected' : ''}`}
            style={builderStyle}
            data-canvas-element-id={element.id}
            data-canvas-element-type={element.type}
            onClick={handleSelect}
          >
            {content}
          </div>
        );
      })}
      {selectedElement ? (
        <SelectionBox
          element={selectedElement}
          frame={frameSize}
          onChangeElementFrame={onChangeElementFrame}
        />
      ) : null}
    </div>
  );

  if (!responsiveScaleEnabled) return frame;

  return (
    <div
      ref={responsiveShellRef}
      className="ce-canvas-scale"
      style={{
        width: '100%',
        height: num(canvas.height, 0) * responsiveScale,
        display: 'flex',
        justifyContent: 'center',
        overflow: 'visible',
      }}
    >
      {frame}
    </div>
  );
}
